// Man hinh dang nhap + thiet lap tai khoan moi.
//
// Khong co bo tai khoan rieng cho phan mem: dang nhap bang chinh tai khoan SKU
// duoc cap. Lan dau tien phai qua mot buoc dang nhap THAT vao panel, co nguoi
// tick captcha - do cung la cach duy nhat kiem chung tai khoan co dung khong.

import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { Alert, Badge, ErrorBox, Spinner } from "../components/ui";
import KhungVNC from "../components/KhungVNC";
import type { PhienPanel } from "../types";

// Nhan ngan cho dau the. Thong diep day du (co the rat dai) de trong o canh bao.
const NHAN_TRANG_THAI: Record<string, string> = {
  dang_mo: "Đang mở trình duyệt",
  cho_nguoi: "Chờ bạn tick captcha",
  xong: "Thành công",
  hong: "Thất bại",
  het_gio: "Hết giờ",
  huy: "Đã huỷ",
};

function KhungCaptcha({
  phien,
  onXong,
  onHuy,
}: {
  phien: PhienPanel;
  onXong: () => void;
  onHuy: () => void;
}) {
  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [phien.log]);

  const mau =
    phien.trang_thai === "xong"
      ? "ok"
      : phien.trang_thai === "cho_nguoi" || phien.trang_thai === "dang_mo"
        ? "info"
        : "danger";

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2 className="grow">Đăng nhập panel cho {phien.tai_khoan}</h2>
        <Badge tone={mau as "ok" | "info" | "danger"} title={phien.thong_diep}>
          {NHAN_TRANG_THAI[phien.trang_thai] ?? phien.trang_thai}
        </Badge>
        {!phien.xong ? (
          <span className="small muted">còn {phien.con_lai}s</span>
        ) : null}
      </div>

      <div className="card-body">
        {phien.trang_thai === "xong" ? (
          <Alert tone="ok">{phien.thong_diep}</Alert>
        ) : phien.xong ? (
          <Alert tone="danger">{phien.thong_diep}</Alert>
        ) : (
          <Alert tone="info">
            Bên dưới là cửa sổ Chrome đang chạy trên máy chủ. Tick vào ô{" "}
            <strong>“Tôi không phải người máy”</strong> (nếu Google hỏi thêm thì chọn ảnh).
            Xong là hệ thống tự đăng nhập và lưu phiên riêng cho bạn.
          </Alert>
        )}

        {phien.du_an.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            <div className="section-title">Dự án tài khoản này truy cập được</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {phien.du_an.map((d, i) => (
                <li key={i} className="small">{d}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {!phien.xong && phien.vnc_url ? (
          <KhungVNC url={phien.vnc_url} sanSang={phien.vnc_san_sang} cao={620} />
        ) : null}

        <div
          ref={logRef}
          className="console-body"
          style={{
            marginTop: 14,
            maxHeight: 150,
            background: "#171b23",
            borderRadius: "var(--radius)",
          }}
        >
          {phien.log.map((d, i) => (
            <div key={i} className="console-line">
              <span>{d}</span>
            </div>
          ))}
        </div>

        <div className="row" style={{ marginTop: 14 }}>
          {phien.trang_thai === "xong" ? (
            <button className="btn btn-primary" onClick={onXong}>
              Vào phần mềm
            </button>
          ) : (
            <button className="btn" onClick={onHuy}>
              {phien.xong ? "Đóng" : "Huỷ"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DangNhapPage() {
  const { dat, napLai } = useAuth();
  const [taiKhoan, setTaiKhoan] = useState("");
  const [matKhau, setMatKhau] = useState("");
  const [loi, setLoi] = useState<unknown>(null);
  const [dangGui, setDangGui] = useState(false);
  const [goiThietLap, setGoiThietLap] = useState(false);
  const [phien, setPhien] = useState<PhienPanel | null>(null);
  const [vncSanSang, setVncSanSang] = useState(true);

  useEffect(() => {
    api.health()
      .then((h) => setVncSanSang(h.vnc_san_sang !== false))
      .catch(() => undefined);
  }, []);

  // Theo doi phien captcha cho den khi xong.
  useEffect(() => {
    if (!phien || phien.xong) return;
    const t = window.setInterval(async () => {
      try {
        const r = await api.xemPhienPanel(phien.id);
        setPhien(r.phien);
      } catch {
        window.clearInterval(t);
      }
    }, 1500);
    return () => window.clearInterval(t);
  }, [phien?.id, phien?.xong]);

  const dangNhap = async () => {
    setLoi(null);
    setGoiThietLap(false);
    setDangGui(true);
    try {
      const r = await api.dangNhap(taiKhoan, matKhau);
      dat(r.nguoi_dung);
      await napLai();
    } catch (e) {
      setLoi(e);
      const p = e instanceof ApiError ? (e.payload as { can_dang_ky?: boolean }) : null;
      if (p?.can_dang_ky) setGoiThietLap(true);
    } finally {
      setDangGui(false);
    }
  };

  const thietLap = async () => {
    setLoi(null);
    setDangGui(true);
    try {
      const r = await api.moPhienPanel(taiKhoan, matKhau);
      setPhien(r.phien);
    } catch (e) {
      setLoi(e);
    } finally {
      setDangGui(false);
    }
  };

  const hoanTat = async () => {
    if (!phien) return;
    try {
      const r = await api.nhanTheSauPhien(phien.id);
      dat(r.nguoi_dung);
      await napLai();
    } catch (e) {
      setLoi(e);
    }
  };

  const huyPhien = async () => {
    if (phien && !phien.xong) await api.huyPhienPanel(phien.id).catch(() => undefined);
    setPhien(null);
  };

  if (phien) {
    return (
      <div style={{ maxWidth: 940, margin: "40px auto", padding: "0 20px" }}>
        <KhungCaptcha phien={phien} onXong={hoanTat} onHuy={huyPhien} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: "80px auto", padding: "0 20px" }}>
      <div className="row" style={{ justifyContent: "center", marginBottom: 22 }}>
        <div className="rail-logo" style={{ width: 40, height: 40, fontSize: 15 }}>
          SKU
        </div>
        <div>
          <div style={{ fontWeight: 650, fontSize: 18 }}>Auto SKU</div>
          <div className="small muted">Đăng nhập bằng tài khoản SKU của bạn</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="field">
            <label className="label"><span className="req">*</span>Tài khoản</label>
            <input
              className="input"
              value={taiKhoan}
              autoFocus
              placeholder="ví dụ: CSCN15"
              onChange={(e) => setTaiKhoan(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && matKhau && dangNhap()}
            />
          </div>
          <div className="field">
            <label className="label"><span className="req">*</span>Mật khẩu</label>
            <input
              className="input"
              type="password"
              value={matKhau}
              onChange={(e) => setMatKhau(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && taiKhoan && dangNhap()}
            />
          </div>

          {loi ? <div style={{ marginBottom: 12 }}><ErrorBox error={loi} /></div> : null}

          {goiThietLap ? (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="info">
                Tài khoản này chưa được thiết lập trên hệ thống. Bấm{" "}
                <strong>Thiết lập tài khoản</strong> để đăng nhập panel một lần —
                bạn sẽ cần tick captcha.
              </Alert>
            </div>
          ) : null}

          {!vncSanSang ? (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="warn">
                Máy chủ chưa có công cụ hiển thị trình duyệt, nên chưa thiết lập được
                tài khoản mới. Dựng lại image: <code className="mono">docker compose build backend</code>
              </Alert>
            </div>
          ) : null}

          <button
            className="btn btn-primary btn-block"
            onClick={dangNhap}
            disabled={!taiKhoan.trim() || !matKhau || dangGui}
          >
            {dangGui ? <Spinner /> : null} Đăng nhập
          </button>

          <button
            className="btn btn-block"
            style={{ marginTop: 8 }}
            onClick={thietLap}
            disabled={!taiKhoan.trim() || !matKhau || dangGui || !vncSanSang}
          >
            Thiết lập tài khoản / đăng nhập lại panel
          </button>

          <div className="hint" style={{ textAlign: "center", marginTop: 12 }}>
            Mật khẩu chỉ dùng để đăng nhập panel, được mã hoá khi lưu.
          </div>
        </div>
      </div>
    </div>
  );
}
